import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import PieChart from '../../../charts/echarts/PieChart'
import ChartGraphic from '../../../charts/echarts/ChartGraphic'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'

const useStyles = makeStyles(() => ({
  comparisonCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: '0.5rem',
    backgroundColor: 'var(--color-neutral-50)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  alignment: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-bold)'
  },
  header: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-neutral-700)'
  },
  description: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-500)'
  }
}))

function ComparisonCard ({
  className,
  style,
  alignmentPrefix = 'With',
  alignmentPerson,
  header,
  description,
  overallCompatibilityScore,
  displayItems = ['alignment', 'header', 'overallCompatibilityScore', 'description'],
  chartProps = {},
  seriesProps = {},
  scoreFormat = '0[.]0%',
  compatabilityLabel = 'Compatibility'
}) {
  const { formatter } = useFormattingContext()
  const classes = useStyles()

  const seriesConfig = useMemo(() => {
    return {
      radius: ['75%', '90%'],
      label: {
        show: false
      },
      ...(seriesProps ?? {})
    }
  }, [seriesProps])

  const chartConfig = useMemo(() => {
    return {
      tooltip: {
        trigger: 'item'
      },
      height: 100,
      width: 100,
      ...chartProps
    }
  }, [chartProps])

  const chartData = useMemo(() => (
    [{
      name: compatabilityLabel,
      value: overallCompatibilityScore / 100,
      itemStyle: {
        color: '#212945'
      }
    },
    {
      name: '',
      value: (100 - overallCompatibilityScore) / 100,
      itemStyle: {
        color: '#CCCCCC'
      }
    }]), [compatabilityLabel, overallCompatibilityScore])

  return (
    <div className={clsx('flex gap-md items-center', className)} style={style}>
      <div>
        <ChartProvider {...chartConfig}>
          <PieChart
            seriesId='compatibility-score'
            data={chartData}
            {...seriesConfig}
          />
          <ChartGraphic
            seriesId='compatibility-score-graphic'
            type='text'
            top='center'
            left='center'
            z={50}
            style={{
              textAlign: 'center',
              fontFamily: 'GothamPro, Gotham-Book, "Open Sans", Tahoma, "Segoe UI", sans-serif',
              fill: '#000',
              fontSize: '1.2rem',
              text: formatter(overallCompatibilityScore / 100, scoreFormat)
            }}
          />
        </ChartProvider>
      </div>

      {displayItems.some(item => ['alignment', 'header', 'description'].includes(item)) && (
        <div>
          {displayItems.includes('alignment') && (<div className={classes.header}>{header}</div>)}
          {displayItems.includes('header') && (<div className={classes.alignment}>{alignmentPrefix} {alignmentPerson}</div>)}
          {displayItems.includes('description') && (<div className={classes.description}>{description}</div>)}
        </div>
      )}
    </div>
  )
}

ComparisonCard.propTypes = {
  style: PropTypes.object,
  className: PropTypes.any,
  alignmentPrefix: PropTypes.string,
  alignmentPerson: PropTypes.string,
  header: PropTypes.string,
  description: PropTypes.string,
  displayItems: PropTypes.arrayOf(PropTypes.string),
  overallCompatibilityScore: PropTypes.number,
  chartProps: PropTypes.object,
  seriesProps: PropTypes.object,
  scoreFormat: PropTypes.string,
  compatabilityLabel: PropTypes.string
}

export default ComparisonCard
